import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { getAppConfig } from "@config/appConfig";
import { ApplicationContext } from "@context/ApplicationContext";
import LazyImage from "@components/Ui/LazyImage";
import { getDistance } from "@utils/locationUtils";
// Reuse the Cinemas page grid/card styling
import "@components/V3/V3ParticipatingCinemas/style.scss";

const MAX_NEARBY = 12;

const VenueNearbyCinemas = ({ venue }) => {
    const { cinemas } = useContext(ApplicationContext);
    const config = getAppConfig();
    const fallbackImage = `${config?.cdnUrl}asset/img/default-movie-poster-small.webp`;

    const nearbyCinemas = useMemo(() => {
        if (!Array.isArray(cinemas)) return [];

        // Exclude this venue and any cinema whose group also hides its sessions
        const candidates = cinemas.filter(
            (cinema) =>
                Number(cinema.id) !== Number(venue?.id) &&
                Number(cinema.hide_sessions_show_nearby) !== 1
        );

        const lat = parseFloat(venue?.latitude);
        const lng = parseFloat(venue?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return candidates.slice(0, MAX_NEARBY);
        }

        return candidates
            .map((cinema) => ({
                ...cinema,
                distance: getDistance(lat, lng, cinema.latitude, cinema.longitude),
            }))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, MAX_NEARBY);
    }, [cinemas, venue?.id, venue?.latitude, venue?.longitude]);

    if (nearbyCinemas.length === 0) return null;

    return (
        <div className="participating-cinemas__list">
            {nearbyCinemas.map((cinema) => (
                <Link
                    key={cinema.id}
                    className="participating-cinemas__card"
                    to={{ pathname: cinema.link_url }}
                >
                    <div className="participating-cinemas__image-wrapper">
                        <LazyImage
                            className="participating-cinemas__image"
                            src={cinema.thumbnail_url ?? cinema.large_image_url}
                            alt={cinema.name}
                            fallbackSrc={fallbackImage}
                            fetchpriority="low"
                        />
                    </div>
                    <div className="participating-cinemas__info">
                        <h2 className="participating-cinemas__title">
                            {cinema.name}
                            {Number.isFinite(cinema.distance) && (
                                <span className="participating-cinemas__distance">
                                    {" "}({cinema.distance.toFixed(1)} km)
                                </span>
                            )}
                        </h2>
                        <address className="participating-cinemas__address">
                            {[cinema.info_address, cinema.info_state, cinema.info_postcode]
                                .filter(Boolean)
                                .join(", ")}
                        </address>
                    </div>
                    <span className="participating-cinemas__background"></span>
                </Link>
            ))}
        </div>
    );
};

export default VenueNearbyCinemas;
